import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

function formatLocalDateYYYYMMDD(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const SearchContext = createContext();

export const useSearch = () => useContext(SearchContext);

export const SearchProvider = ({ children }) => {
  const todayStr = formatLocalDateYYYYMMDD();

  const [draftSearchQuery, setDraftSearchQuery] = useState('');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');

  const [draftStartDate, setDraftStartDate] = useState(todayStr);
  const [appliedStartDate, setAppliedStartDate] = useState(todayStr);

  const [draftEndDate, setDraftEndDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');

  const extraApplyRef = useRef(null);
  const extraClearRef = useRef(null);

  const registerExtraFilterApply = useCallback((fn) => {
    extraApplyRef.current = fn;
    return () => {
      if (extraApplyRef.current === fn) extraApplyRef.current = null;
    };
  }, []);

  const registerExtraFilterClear = useCallback((fn) => {
    extraClearRef.current = fn;
    return () => {
      if (extraClearRef.current === fn) extraClearRef.current = null;
    };
  }, []);

  const applyFilters = useCallback(() => {
    setAppliedSearchQuery(draftSearchQuery);
    setAppliedStartDate(draftStartDate);
    setAppliedEndDate(draftEndDate);
    extraApplyRef.current?.();
  }, [draftSearchQuery, draftStartDate, draftEndDate]);

  const resetListFilters = useCallback(({ resetDates = false } = {}) => {
    setDraftSearchQuery('');
    setAppliedSearchQuery('');
    if (resetDates) {
      const today = formatLocalDateYYYYMMDD();
      setDraftStartDate(today);
      setAppliedStartDate(today);
      setDraftEndDate('');
      setAppliedEndDate('');
    }
  }, []);

  const clearFilters = useCallback(() => {
    resetListFilters({ resetDates: true });
    extraClearRef.current?.();
  }, [resetListFilters]);

  return (
    <SearchContext.Provider
      value={{
        // Valores aplicados (usados en API / listados)
        searchQuery: appliedSearchQuery,
        startDate: appliedStartDate,
        endDate: appliedEndDate,
        // Borrador (controles de filtro)
        draftSearchQuery,
        draftStartDate,
        draftEndDate,
        updateDraftSearchQuery: setDraftSearchQuery,
        updateDraftStartDate: setDraftStartDate,
        updateDraftEndDate: setDraftEndDate,
        // Compatibilidad: escribir borrador sin aplicar
        updateSearchQuery: setDraftSearchQuery,
        updateStartDate: setDraftStartDate,
        updateEndDate: setDraftEndDate,
        applyFilters,
        clearFilters,
        resetListFilters,
        registerExtraFilterApply,
        registerExtraFilterClear,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
